import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useApi from '../../hooks/useApi';
import NodeContentTopSection from '../NodeContentTopSection';
import NodeContentHeaderSection from '../NodeContentHeaderSection';
import NodeContentFooterSection from '../NodeContentFooterSection';
import NodeContentSection from '../NodeContentSection';
import ChunkContentPreview from './nodes/ChunkContentPreview';
import TaskContentPreview from './nodes/TaskContentPreview';
import WrittenQuizContentPreview from './nodes/WrittenQuizContentPreview';
import QuizContentPreview from './nodes/QuizContentPreview';
import ChecklistContentPreview from './nodes/ChecklistContentPreview';
import EvaluationContentPreview from './nodes/EvaluationContentPreview';
import BlogContentPreview from './nodes/BlogContentPreview';
import GlossaryContentPreview from './nodes/GlossaryContentPreview';
import CorrelationContentPreview from './nodes/CorrelationContentPreview';
import { PanelWrapper } from './styles';

const NodeContentPreview = ({ nodeId, type: baseType, userToken, navigation, panel }) => {
  const { getData, loading } = useApi(userToken);
  const [data, setData] = useState();
  const [content, setContent] = useState();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [contextId, setContextId] = useState('');

  const getKey = (type) => {
    switch (type) {
      case 'private_blog':
        return 'logbook';
      case 'public_blog':
        return 'noticeboard';
      case 'legend':
        return 'correlation';
      case 'correlation':
        return 'correlation_quiz';
      default:
        return type;
    }
  };

  const isArticle = ['chunk', 'video', 'link'].includes(baseType);
  const previewText = i18n.__(`${getKey(isArticle ? 'chunk' : type)}_preview_text`);

  useEffect(() => {
    const getNodeData = async () => {
      if (!loading) {
        const nodeType = isArticle ? 'chunks' : 'behaviors';
        const response = await getData(`/api/v3/viewer/${nodeType}/${nodeId}/preview`);
        if (response.success) {
          const body = response.body;
          setType(body.type || '');
          setTitle(body.name || '');
          setDescription((isArticle ? body.data.content : body.data.body) || '');
          setContextId(body.data.uuid);
          setData(body.data);
          setContent(body);
        }
      }
    };
    getNodeData();
  }, []);

  if (!data) {
    return null;
  }

  const topSection = (
    <NodeContentTopSection
      nodeType={type}
      showFullScreenTool={true}
      showVoiceOverTool={true}
      panel={panel}
      userToken={userToken}
      nodeData={content}
      viewer={window.viewer}
      nodeId={nodeId}
      title={title}
    />
  );

  const headerSection = <NodeContentHeaderSection title={title} />;

  const contentSection = (
    <NodeContentSection description={description} type={isArticle ? 'chunk' : type} contextId={contextId} />
  );

  const previewSection = () => {
    const getPreviewComponent = () => {
      switch (type) {
        case 'chunk':
        case 'video':
        case 'link':
          return ChunkContentPreview;
        case 'task':
        case 'group':
          return TaskContentPreview;
        case 'written_quiz':
          return WrittenQuizContentPreview;
        case 'quiz':
          return QuizContentPreview;
        case 'checklist':
          return ChecklistContentPreview;
        case 'evaluation':
          return EvaluationContentPreview;
        case 'private_blog':
        case 'public_blog':
          return BlogContentPreview;
        case 'legend':
          return GlossaryContentPreview;
        case 'correlation':
          return CorrelationContentPreview;
      }
    };
    const PreviewComponent = getPreviewComponent();
    return <PreviewComponent type={type} data={data} previewText={previewText} />;
  };

  const useCommonSubmit = [
    'chunk',
    'video',
    'link',
    'written_quiz',
    'quiz',
    'private_blog',
    'legend',
    'correlation',
  ].includes(type);

  const footerSection = <NodeContentFooterSection navigation={navigation} useCommonSubmit={useCommonSubmit} />;

  return (
    !loading && (
      <PanelWrapper className={'ir-content'}>
        {topSection}
        {headerSection}
        {contentSection}
        {previewSection()}
        {footerSection}
      </PanelWrapper>
    )
  );
};

export default NodeContentPreview;

NodeContentPreview.propTypes = {
  nodeId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
  navigation: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
};
